import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import TabView from "../../../../components/atoms/tabView/TabView.atom"
import TabPanel from "../../../../components/atoms/tabView/TabPanel.atom"
import VacationFlag from "../../../../components/molecules/VacationFlag.molecule"
import {
  getYearHolidays,
  saveYearHolidays,
} from "../../../../services/vacation.service"
import { HOLIDAYS_MODALS_TABS } from "../../../../consts/modules/vacations.consts"
import useToast from "../../../../hooks/useToast.hook"
import HolidaysTable from "../../components/HolidaysTable.compound"
import SecondaryButton from "../../../../components/molecules/buttons/SecondaryButton.molecule"
import PrimaryButton from "../../../../components/molecules/buttons/PrimaryButton.molecule"
import { areObjectsEqual } from "../../../../utils/object.utils"

const ModalFormHolidaysManagement = ({
  visible,
  setVisible,
  year,
  onComplete,
}) => {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [holidays, setHolidays] = useState({
    [HOLIDAYS_MODALS_TABS.OFICIALES]: [],
    [HOLIDAYS_MODALS_TABS.FERIADOS_PLUS]: [],
    [HOLIDAYS_MODALS_TABS.GLOBALES_SISU]: [],
  })
  const [initialHolidays, setInitialHolidays] = useState({
    [HOLIDAYS_MODALS_TABS.OFICIALES]: [],
    [HOLIDAYS_MODALS_TABS.FERIADOS_PLUS]: [],
    [HOLIDAYS_MODALS_TABS.GLOBALES_SISU]: [],
  })
  const { setErrorMessage } = useToast()
  useEffect(() => {
    if (!visible) return
    fetchData()
  }, [visible])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getYearHolidays(year)
      if (!response.success) {
        throw new Error(response.message)
      }
      setHolidays((prev) => ({
        ...prev,
        ...response.result,
      }))

      setInitialHolidays((prev) => ({
        ...prev,
        ...response.result,
      }))
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: "Ha ocurrido un error al obtener los días feriados.",
      })
      setVisible(false)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const response = await saveYearHolidays(year, holidays)
      if (!response.success) {
        throw new Error(response.message)
      }
      onComplete && onComplete()
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: "Ha ocurrido un error al guardar los días feriados.",
      })
      setVisible(false)
    } finally {
      setSubmitting(false)
      setVisible(false)
    }
  }
  const tabHeaderTemplate = ({ schemaTitle, label }) => {
    return (
      <div className="flex gap-2 align-items-center">
        <VacationFlag schemaTitle={schemaTitle} />
        <span>{label}</span>
      </div>
    )
  }

  const modalHeaderTemplate = ({ year }) => (
    <div className="flex gap-3 align-items-center">
      <i
        className={`pi pi-pencil text-4xl`}
        style={{ color: "var(--clr-info__dark)" }}
      />
      <div className="flex flex-column gap-2">
        <span className="text-3xl font-semibold font-cairo line-height-1">
          Días feriados
        </span>
        <span className="text-base font-normal font-mulish">{year}</span>
      </div>
    </div>
  )

  const ModalFooterTemplate = () => {
    return (
      <div>
        <SecondaryButton
          rounded
          icon="pi pi-times"
          label="Cancelar"
          onClick={() => setVisible(false)}
        />
        <PrimaryButton
          rounded
          icon={`pi ${submitting ? "pi-spin pi-spinner" : "pi-check"}`}
          label={"Aceptar"}
          onClick={handleSubmit}
          disabled={
            loading ||
            Object.values(holidays)
              .flat()
              .some((h) => h.date === null) ||
            submitting ||
            areObjectsEqual(holidays, initialHolidays)
          }
        />
      </div>
    )
  }
  return (
    <ModalForm
      className="holiday-management-modal-form"
      visible={visible}
      setVisible={setVisible}
      header={modalHeaderTemplate({ year })}
      service={handleSubmit}
      footer={ModalFooterTemplate()}
    >
      <TabView>
        <TabPanel
          header={tabHeaderTemplate({
            schemaTitle: "Nacional v2024",
            label: "Feriados oficiales",
          })}
        >
          <HolidaysTable
            year={year}
            loading={loading}
            newValuePlaceholder="Nuevo feriado oficial"
            value={holidays[HOLIDAYS_MODALS_TABS.OFICIALES]}
            allHolidays={[
              ...holidays[HOLIDAYS_MODALS_TABS.FERIADOS_PLUS],
              ...holidays[HOLIDAYS_MODALS_TABS.OFICIALES],
              ...holidays[HOLIDAYS_MODALS_TABS.GLOBALES_SISU],
            ]}
            onChange={(e) =>
              setHolidays({ ...holidays, [HOLIDAYS_MODALS_TABS.OFICIALES]: e })
            }
            deletableRows
          />
        </TabPanel>
        <TabPanel
          header={tabHeaderTemplate({
            schemaTitle: "Nacional v2024",
            label: "Feriados +",
          })}
        >
          <HolidaysTable
            year={year}
            newValuePlaceholder="Nuevo feriado +"
            value={holidays[HOLIDAYS_MODALS_TABS.FERIADOS_PLUS]}
            allHolidays={[
              ...holidays[HOLIDAYS_MODALS_TABS.FERIADOS_PLUS],
              ...holidays[HOLIDAYS_MODALS_TABS.OFICIALES],
              ...holidays[HOLIDAYS_MODALS_TABS.GLOBALES_SISU],
            ]}
            onChange={(e) =>
              setHolidays({
                ...holidays,
                [HOLIDAYS_MODALS_TABS.FERIADOS_PLUS]: e,
              })
            }
            deletableRows
          />
        </TabPanel>
        <TabPanel
          header={tabHeaderTemplate({
            schemaTitle: "Sisu",
            label: "Globales Sisu",
          })}
        >
          <HolidaysTable
            year={year}
            newValuePlaceholder="Nuevo feriado Sisu"
            value={holidays[HOLIDAYS_MODALS_TABS.GLOBALES_SISU]}
            allHolidays={[
              ...holidays[HOLIDAYS_MODALS_TABS.FERIADOS_PLUS],
              ...holidays[HOLIDAYS_MODALS_TABS.OFICIALES],
              ...holidays[HOLIDAYS_MODALS_TABS.GLOBALES_SISU],
            ]}
            onChange={(e) =>
              setHolidays({
                ...holidays,
                [HOLIDAYS_MODALS_TABS.GLOBALES_SISU]: e,
              })
            }
            deletableRows
          />
        </TabPanel>
      </TabView>
    </ModalForm>
  )
}

export default ModalFormHolidaysManagement

ModalFormHolidaysManagement.propTypes = {
  visible: PropTypes.bool,
  setVisible: PropTypes.func,
  year: PropTypes.number,
  onComplete: PropTypes.func,
}
